import type { ActiveOptions } from '@tanstack/react-router';

import {
  BotMessageSquare,
  Building,
  ChartLine,
  FlaskConical,
  Globe,
  Grid3x3,
  House,
  LayoutDashboard,
  Lightbulb,
  Settings,
  Signpost,
  Star,
  Store
} from '@brightlocal/icons';

import { FEATURE_FLAGS } from 'shared/consts';
import { LABS } from 'shared/modules/labs';
import { buildLocationDashboardPath, LOCATION_DASHBOARD_SEGMENT } from 'shared/modules/old-platform-redirect';
import { routes } from 'shared/routes';

import { NAV_ITEM_TYPE, type NavGroup, type NavItem } from './types';

/** GTM/GA click-tracking marker on sidebar navigation links. */
export const SIDE_NAV_DATA_ELEMENT_NAME = 'navigation';

/**
 * When a sidebar link counts as the open page: its exact path, whatever the address's search holds, so a page keeps
 * its highlight while its tab or filters are in the address.
 */
export const NAV_LINK_ACTIVE_OPTIONS = { exact: true, includeSearch: false } satisfies ActiveOptions;

/** Matches any location-level path (e.g. `/locations/42/summary`). */
const LOCATION_PATH_PATTERN = /^\/locations\/\d+/;

const isLocationPath = (pathname: string) => LOCATION_PATH_PATTERN.test(pathname);

/** Matches the Account settings pages (e.g. `/account/profile`). */
const ACCOUNT_PATH_PATTERN = /^\/account\//;

const isAccountPath = (pathname: string) => ACCOUNT_PATH_PATTERN.test(pathname);

/** Ids of the sidebar nav groups, in render order. */
export const NAV_GROUP_ID = {
  LOCATIONS: 'locations',
  ACCOUNT: 'account',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  LABS: 'labs'
} as const;

// Top group: general-level entry points, not tied to a single location.
// For now just "All Locations"; future non-location routes land here too.
const LOCATIONS_NAV_ITEMS: readonly NavItem[] = [
  {
    type: NAV_ITEM_TYPE.ROUTER,
    id: 'your-locations',
    labelKey: 'appShell.nav.allLocations',
    to: routes.locations(),
    icon: House
  }
];

// Reports group: location report tools, shown only within a location context.
const REPORTS_NAV_ITEMS: readonly NavItem[] = [
  {
    type: NAV_ITEM_TYPE.ROUTER,
    id: 'location-summary',
    labelKey: 'appShell.nav.locationDashboard',
    to: routes.locationSummary('$id').to,
    toFn: (params) => routes.locationSummary(params.id).to.replace('$id', params.id),
    icon: LayoutDashboard,
    visibleWhen: isLocationPath
  },
  {
    type: NAV_ITEM_TYPE.ROUTER,
    id: 'ai-visibility',
    labelKey: 'appShell.nav.aiVisibility',
    badgeLabelKey: 'appShell.nav.newBadge',
    to: routes.aiVisibility('$id').to,
    toFn: (params) => routes.aiVisibility(params.id).to.replace('$id', params.id),
    icon: BotMessageSquare,
    visibleWhen: isLocationPath
  },
  {
    type: NAV_ITEM_TYPE.OLD_PLATFORM,
    id: 'ai-insights',
    labelKey: 'appShell.nav.insightsActions',
    icon: Lightbulb,
    oldPlatformPathFn: (params) => buildLocationDashboardPath(params.id, LOCATION_DASHBOARD_SEGMENT.AI_INSIGHTS),
    visibleWhen: isLocationPath
  },
  {
    type: NAV_ITEM_TYPE.ROUTER,
    id: 'rankings',
    labelKey: 'appShell.nav.localRankTracker',
    icon: ChartLine,
    to: routes.localRankTracker('$id').to,
    toFn: (params) => routes.localRankTracker(params.id).to.replace('$id', params.id),
    featureFlag: FEATURE_FLAGS.LRT_ALLOWED,
    flagOffOldPlatformPathFn: (params) => buildLocationDashboardPath(params.id, LOCATION_DASHBOARD_SEGMENT.RANKINGS),
    visibleWhen: isLocationPath
  },
  {
    type: NAV_ITEM_TYPE.ROUTER,
    id: 'local-search-grid',
    labelKey: 'appShell.nav.localSearchGrid',
    to: routes.localSearchGrid('$id').to,
    toFn: (params) => routes.localSearchGrid(params.id).to.replace('$id', params.id),
    icon: Grid3x3,
    visibleWhen: isLocationPath
  },
  {
    type: NAV_ITEM_TYPE.COLLAPSIBLE,
    id: 'citations',
    labelKey: 'appShell.nav.citations',
    icon: Signpost,
    visibleWhen: isLocationPath,
    items: [
      {
        type: NAV_ITEM_TYPE.OLD_PLATFORM,
        id: 'citation-builder',
        labelKey: 'appShell.nav.citationBuilder',
        oldPlatformPathFn: (params) => buildLocationDashboardPath(params.id, LOCATION_DASHBOARD_SEGMENT.CITATIONS)
      }
    ]
  },
  {
    type: NAV_ITEM_TYPE.COLLAPSIBLE,
    id: 'gbp',
    labelKey: 'appShell.nav.googleBusinessProfile',
    icon: Store,
    visibleWhen: isLocationPath,
    items: [
      {
        type: NAV_ITEM_TYPE.ROUTER,
        id: 'gbp-insights',
        labelKey: 'appShell.nav.gbpInsights',
        to: routes.gbpInsights('$id').to,
        toFn: (params) => routes.gbpInsights(params.id).to.replace('$id', params.id),
        featureFlag: FEATURE_FLAGS.GBP_INSIGHTS_ALLOWED,
        flagOffOldPlatformPathFn: (params) => buildLocationDashboardPath(params.id, LOCATION_DASHBOARD_SEGMENT.GBP)
      }
    ]
  },
  {
    type: NAV_ITEM_TYPE.COLLAPSIBLE,
    id: 'reviews',
    labelKey: 'appShell.nav.reviews',
    icon: Star,
    visibleWhen: isLocationPath,
    items: [
      {
        type: NAV_ITEM_TYPE.ROUTER,
        id: 'review-tracker',
        labelKey: 'appShell.nav.reviewTracker',
        to: routes.reviewTracker('$id').to,
        toFn: (params) => routes.reviewTracker(params.id).to.replace('$id', params.id),
        featureFlag: FEATURE_FLAGS.REVIEW_TRACKER_ALLOWED
      },
      {
        type: NAV_ITEM_TYPE.ROUTER,
        id: 'review-manager',
        labelKey: 'appShell.nav.reviewManager',
        to: routes.reviewManager('$id').to,
        toFn: (params) => routes.reviewManager(params.id).to.replace('$id', params.id),
        featureFlag: FEATURE_FLAGS.REVIEW_MANAGER_ALLOWED,
        flagOffOldPlatformPathFn: (params) =>
          buildLocationDashboardPath(params.id, LOCATION_DASHBOARD_SEGMENT.REPUTATION_REVIEWS)
      }
    ]
  }
];

// Account group: the Account settings sections, shown only on the Account settings pages.
const ACCOUNT_NAV_ITEMS: readonly NavItem[] = [
  {
    type: NAV_ITEM_TYPE.COLLAPSIBLE,
    id: 'account-settings',
    labelKey: 'appShell.nav.accountSettings',
    icon: Settings,
    featureFlag: FEATURE_FLAGS.ACCOUNT_SETTINGS_ALLOWED,
    visibleWhen: isAccountPath,
    items: [
      {
        type: NAV_ITEM_TYPE.ROUTER,
        id: 'account-profile',
        labelKey: 'appShell.nav.accountProfile',
        to: routes.accountProfile()
      },
      {
        type: NAV_ITEM_TYPE.ROUTER,
        id: 'account-security',
        labelKey: 'appShell.nav.accountSecurity',
        to: routes.accountSecurity()
      },
      {
        type: NAV_ITEM_TYPE.ROUTER,
        id: 'account-company',
        labelKey: 'appShell.nav.accountCompany',
        to: routes.accountCompany()
      },
      {
        type: NAV_ITEM_TYPE.ROUTER,
        id: 'account-users',
        labelKey: 'appShell.nav.accountUsers',
        to: routes.accountUsers()
      }
    ]
  }
];

// Settings group: location settings/admin tools, shown only within a location context.
const SETTINGS_NAV_ITEMS: readonly NavItem[] = [
  {
    type: NAV_ITEM_TYPE.OLD_PLATFORM,
    id: 'location-manager',
    labelKey: 'appShell.nav.locationManager',
    icon: Building,
    oldPlatformPathFn: (params) => buildLocationDashboardPath(params.id, LOCATION_DASHBOARD_SEGMENT.EDIT),
    visibleWhen: isLocationPath
  },
  {
    type: NAV_ITEM_TYPE.OLD_PLATFORM,
    id: 'whitelabel',
    labelKey: 'appShell.nav.whiteLabel',
    icon: Globe,
    oldPlatformPathFn: (params) => buildLocationDashboardPath(params.id, LOCATION_DASHBOARD_SEGMENT.SETTINGS),
    visibleWhen: isLocationPath
  }
];

// Labs group: one row per registered lab, each behind its own flag and the global labs flag,
// only within a location context. Hidden when no lab is switched on.
const LABS_NAV_ITEMS: readonly NavItem[] = [
  {
    type: NAV_ITEM_TYPE.COLLAPSIBLE,
    id: 'labs',
    labelKey: 'appShell.nav.labs',
    icon: FlaskConical,
    featureFlag: FEATURE_FLAGS.LABS_ALLOWED,
    visibleWhen: isLocationPath,
    items: LABS.map(({ id, flag }) => ({
      type: NAV_ITEM_TYPE.ROUTER,
      id: `lab-${id}`,
      labelKey: `appShell.nav.labItems.${id}`,
      to: routes.lab('$id', '$labId').to,
      toFn: (params) => routes.lab(params.id, id).to.replace('$id', params.id).replace('$labId', id),
      featureFlag: flag
    }))
  }
];

/**
 * Ordered sidebar groups rendered in `SidebarContent`, each divided from the next by a separator.
 *
 * - `locations` — account-level entry points, not tied to a single location.
 * - `account` — the Account settings sections, shown only on the Account settings pages.
 * - `reports` — location report tools, shown only within a location context.
 * - `settings` — location settings/admin tools, shown only within a location context.
 * - `labs` — one row per lab switched on, shown only within a location context; hidden when none is on.
 *
 * Groups whose items are all gated out render nothing — no dangling separators.
 */
export const MAIN_NAV_GROUPS: readonly NavGroup[] = [
  { id: NAV_GROUP_ID.LOCATIONS, items: LOCATIONS_NAV_ITEMS },
  { id: NAV_GROUP_ID.ACCOUNT, items: ACCOUNT_NAV_ITEMS },
  { id: NAV_GROUP_ID.REPORTS, items: REPORTS_NAV_ITEMS },
  { id: NAV_GROUP_ID.SETTINGS, items: SETTINGS_NAV_ITEMS },
  { id: NAV_GROUP_ID.LABS, items: LABS_NAV_ITEMS }
];

/** Debounce for the location switcher search — one request per pause in typing, not per keystroke. */
export const LOCATION_SWITCHER_SEARCH_DEBOUNCE_MS = 300;

/** Smallest page the locations list API accepts — enough to read `totalCount` without the rows. */
export const LOCATION_SWITCHER_TOTAL_PROBE_PAGE_SIZE = 1;

/** An account with this many locations in total gets the static trigger — there is nothing to switch to. */
export const LOCATION_SWITCHER_SINGLE_LOCATION_TOTAL = 1;
